import { useEffect, useState } from "react";
import { Truck, Boxes, ListChecks, PackageCheck, ChevronDown, PackageSearch, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Kpi } from "../components/DetailActions";
import { unitLabel, fmtDate } from "../lib/format";
import { VariantBadge } from "../components/VariantBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const DeliveryBadge = ({ delivered, total, onClick }) => {
  const active = delivered > 0;
  const cls = delivered >= total && total > 0 ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100" : active ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border-stone-300 bg-stone-50 text-stone-500";
  if (!active) {
    return <span className={`w-24 shrink-0 whitespace-nowrap border px-2 py-0.5 text-center text-[10px] font-medium ${cls}`}>Teslim: {delivered}/{total}</span>;
  }
  return (
    <button type="button" onClick={onClick} title="Teslimat detaylarını gör" data-testid="delivery-badge-button" className={`w-24 shrink-0 cursor-pointer whitespace-nowrap border px-2 py-0.5 text-center text-[10px] font-medium underline-offset-2 transition-colors hover:underline active:scale-95 ${cls}`}>
      Teslim: {delivered}/{total}
    </button>
  );
};

export default function SupplierOrdersPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [dlg, setDlg] = useState(null);
  const [dQty, setDQty] = useState("");
  const [dDate, setDDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/supplier/my-orders").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openDlg = (p, viewOnly = false) => {
    setDQty("");
    setDDate(new Date().toISOString().slice(0, 10));
    setDlg({ ...p, viewOnly });
  };

  const saveDelivery = async () => {
    setSaving(true);
    try {
      await api.post("/supplier/deliveries", { sku: dlg.sku, variant: dlg.variant || null, name: dlg.name, qty: Number(dQty), date: dDate });
      toast.success(`${dQty} ${unitLabel(dlg.unit)} teslimat kaydedildi`);
      const r = await api.get("/supplier/my-orders");
      setData(r.data);
      setDlg(null);
      setDQty("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Teslimat kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const removeDelivery = async (id) => {
    try {
      await api.delete(`/supplier/deliveries/${id}`);
      toast.success("Teslimat kaydı silindi");
      const r = await api.get("/supplier/my-orders");
      setData(r.data);
      const updated = r.data.groups.flatMap((g) => g.products).find((p) => p.sku === dlg.sku && (p.variant || "") === (dlg.variant || ""));
      setDlg(updated || null);
    } catch {
      toast.error("Kayıt silinemedi");
    }
  };

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="supplier-orders-loading">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
      </div>
    );
  if (!data)
    return <p className="border border-red-300 bg-red-50 p-4 text-sm text-red-700" data-testid="supplier-orders-error">Veriler yüklenemedi.</p>;

  const remaining = dlg ? dlg.qty - (dlg.deliveredQty || 0) : 0;

  return (
    <div className="space-y-4" data-testid="supplier-orders-page">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi icon={Truck} label="Tedarikçi" value={data.supplierName || "—"} testId="supplier-kpi-name" />
        <Kpi icon={ListChecks} label="Sipariş Satırı" value={data.lineCount} testId="supplier-kpi-lines" />
        <Kpi icon={Boxes} label="Toplam Miktar" value={data.totalQty} testId="supplier-kpi-qty" />
        <Kpi icon={PackageCheck} label="Teslim Edilen" value={`${data.deliveredQty ?? 0}/${data.totalQty}`} testId="supplier-kpi-delivered" />
      </div>

      {(data.groups || []).length === 0 ? (
        <div className="flex flex-col items-center gap-2 border border-stone-200 bg-white py-16 text-stone-400" data-testid="supplier-orders-empty">
          <PackageSearch size={28} strokeWidth={1.5} />
          <p className="text-sm">Henüz size atanmış sipariş bulunmuyor.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.groups.map((g) => (
            <div key={g.category} className="border border-stone-200 bg-white" data-testid={`supplier-group-${g.category.replace(/\s+/g, "_")}`}>
              <button
                type="button"
                onClick={() => setCollapsed((c) => ({ ...c, [g.category]: !c[g.category] }))}
                className="flex w-full items-center gap-2 bg-stone-100 px-4 py-2.5 text-left transition-colors hover:bg-stone-200/60"
                data-testid={`supplier-group-toggle-${g.category.replace(/\s+/g, "_")}`}
              >
                <ChevronDown size={14} className={`text-stone-500 transition-transform duration-200 ${collapsed[g.category] ? "-rotate-90" : ""}`} />
                <span className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-[#1C4E35]">{g.category}</span>
                <span className="text-[11px] text-stone-500">{g.products.length} ürün · {g.qty} adet</span>
                <span className={`ml-auto shrink-0 whitespace-nowrap border px-2 py-0.5 text-[10px] font-medium ${g.deliveredQty >= g.qty ? "border-green-500 bg-green-50 text-green-700" : g.deliveredQty > 0 ? "border-amber-400 bg-amber-50 text-amber-800" : "border-stone-300 bg-white text-stone-500"}`}>
                  Teslim: {g.deliveredQty}/{g.qty}
                </span>
              </button>
              {!collapsed[g.category] && (
                <div>
                  {g.products.map((p, i) => (
                    <div
                      key={`${p.sku}|${p.variant || ""}`}
                      className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 ${i > 0 ? "border-t border-stone-100" : ""}`}
                      data-testid={`supplier-product-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                    >
                      <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                        <p className="flex items-center gap-2 text-sm font-medium">
                          <span className="truncate">{p.name}</span>
                          {p.variant && <VariantBadge variant={p.variant} />}
                        </p>
                        <p className="text-[11px] text-stone-400">
                          {p.sku} · {p.lineCount} sipariş satırı
                          {p.deliveries.length > 0 && ` · ${p.deliveries.length} teslimat kaydı`}
                        </p>
                      </div>
                      <span className="font-heading shrink-0 text-lg font-black tracking-tight text-[#1C4E35]">
                        {p.qty} <span className="text-xs font-medium text-stone-500">{unitLabel(p.unit)}</span>
                      </span>
                      <DeliveryBadge delivered={p.deliveredQty || 0} total={p.qty} onClick={() => openDlg(p, true)} />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDlg(p)}
                        data-testid={`delivery-open-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                        className="ml-auto h-8 shrink-0 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95 sm:ml-0"
                      >
                        <CalendarPlus size={13} className="mr-1.5" /> Teslimat Gir
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-sm" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">{dlg?.viewOnly ? "Teslimat Geçmişi" : "Teslimat Kaydı"}</DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <p className="text-sm">
                <span className="font-medium">{dlg.name}</span>{dlg.variant ? ` — ${dlg.variant}` : ""}
                <span className="ml-2 text-xs text-stone-500">Toplam {dlg.qty} {unitLabel(dlg.unit)} · Kalan <strong className={remaining > 0 ? "text-amber-700" : "text-green-700"}>{remaining}</strong></span>
              </p>
              {!dlg.viewOnly && (remaining > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-stone-600">Teslim Tarihi</Label>
                    <Input type="date" value={dDate} onChange={(e) => setDDate(e.target.value)} data-testid="delivery-date-input" className="rounded-sm border-stone-300" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-stone-600">Miktar (en çok {remaining})</Label>
                    <Input type="number" min="1" max={remaining} value={dQty} onChange={(e) => setDQty(e.target.value)} placeholder="örn. 22" data-testid="delivery-qty-input" className={`rounded-sm ${Number(dQty) > remaining ? "border-red-500 focus-visible:ring-red-500" : "border-stone-300"}`} />
                  </div>
                </div>
              ) : (
                <p className="border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-700" data-testid="delivery-complete-note">Bu ürünün tamamı teslim edildi ✓</p>
              ))}
              {!dlg.viewOnly && remaining > 0 && Number(dQty) > remaining && (
                <p className="border border-red-300 bg-red-50 px-3 py-2 text-xs font-medium text-red-600" data-testid="delivery-qty-error">Girilen miktar kalan adedi ({remaining}) aşamaz.</p>
              )}
              <div className="border border-stone-200 bg-stone-50 px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Teslimat Geçmişi</p>
                {dlg.deliveries.length === 0 ? (
                  <p className="mt-1 text-[11px] text-stone-400">Henüz teslimat girilmedi.</p>
                ) : (
                  <ul className="mt-1.5 max-h-32 space-y-1 overflow-y-auto">
                    {dlg.deliveries.map((d) => (
                      <li key={d.id} className="flex items-center gap-2 text-xs text-stone-600" data-testid={`delivery-entry-${d.id}`}>
                        <span className="font-medium text-[#1C4E35]">{d.qty} {unitLabel(dlg.unit)}</span>
                        <span className="text-stone-400">{fmtDate(d.date)}</span>
                        {d.status === "pending" ? (
                          <span className="border border-amber-300 bg-amber-50 px-1 py-px text-[9px] font-medium text-amber-700">Mal kabul bekliyor</span>
                        ) : (
                          <span className="border border-green-300 bg-green-50 px-1 py-px text-[9px] font-medium text-green-700">Mal kabul edildi</span>
                        )}
                        {d.status === "pending" && (
                          <button type="button" onClick={() => removeDelivery(d.id)} title="Kaydı sil" data-testid={`delivery-delete-${d.id}`} className="ml-auto text-red-500 hover:text-red-700">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDlg(null)} className="rounded-sm border-stone-300">Kapat</Button>
                {!dlg.viewOnly && remaining > 0 && (
                  <Button onClick={saveDelivery} disabled={saving || !dQty || Number(dQty) <= 0 || Number(dQty) > remaining} data-testid="delivery-save-button" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
                    {saving ? "Kaydediliyor..." : "Kaydet"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
